import {
   Modal,
   ModalOverlay,
   ModalContent,
   useColorModeValue,
   ModalHeader,
   ModalCloseButton,
   ModalBody,
   ModalFooter,
   Flex,
   Box,
   Button,
   Table,
   Thead,
   Tbody,
   Tr,
   Th,
   Td,
   Checkbox,
   TableContainer,
   Text,
   Badge,
   Divider,
   Icon,
   useToast,
} from '@chakra-ui/react';
import React, { useState, useMemo, useEffect } from 'react';
import { useCallback } from 'react';
import { CheckIcon } from '@chakra-ui/icons';
import InputField from 'components/ui/InputField';

const SelectTakaDetail = ({ isOpen, onClose, onCloseComplete, takaDetails = [], handleSaveTakaDetail, lineItem, currentIdx, formType, invoiceType = "issue" }) => {
   const toast = useToast();

   const takaDetailsWithIds = useMemo(() => {
      if (!lineItem || currentIdx === null || currentIdx === undefined || !lineItem[currentIdx]) {
         return [];
      }
      return takaDetails?.map((item) => ({
         ...item,
         id: item.id,
      }))
   }, [takaDetails, lineItem, currentIdx]);

   const [selectedIds, setSelectedIds] = useState([]);
   const [quantities, setQuantities] = useState({});
   const [errors, setErrors] = useState({});
   const [editingId, setEditingId] = useState(null);

   const handleOnClose = useCallback(() => {
      onClose();
      setSelectedIds([]);
      setQuantities({});
      setErrors({});
      setEditingId(null);
   }, [onClose]);

   const toggleItemSelection = useCallback((id) => {
      setSelectedIds((prev) => {
         if (prev.includes(id)) {
            return prev.filter((itemId) => itemId !== id);
         } else {
            return [...prev, id];
         }
      });
   }, []);

   const selectAll = useCallback(() => {
      if (selectedIds.length === takaDetailsWithIds.length) {
         // setSelectedIds([]);
         setSelectedIds(takaDetailsWithIds.filter((item) => item.minQty3 > 0).map((item) => item.id));
      } else {
         setSelectedIds(takaDetailsWithIds.map((item) => item.id));
      }
   }, [takaDetailsWithIds.length, selectedIds.length]);

   const handleQuantityChange = useCallback((id, value, maxQty3) => {
      const numValue = Number(value || 0);
      setQuantities((prev) => ({
         ...prev,
         [id]: numValue,
      }));

      if (numValue > maxQty3) {
         setErrors((prev) => ({
            ...prev,
            [id]: `Maximum allowed quantity is ${maxQty3}`,
         }));
      } else {
         setErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[id];
            return newErrors;
         });
      }
   }, []);

   const totalSelected = useMemo(() => {
      return takaDetailsWithIds
         .filter((item) => selectedIds.includes(item.id))
         .reduce((sum, item) => sum + Number(quantities[item.id] ?? item.quantity3 ?? 0), 0);
   }, [selectedIds, takaDetailsWithIds, quantities]);

   const allSelected = selectedIds.length === takaDetailsWithIds.length && takaDetailsWithIds.length > 0;
   const isIndeterminate = selectedIds.length > 0 && selectedIds.length < takaDetailsWithIds.length;

   const hasErrors = useMemo(() => Object.keys(errors).length > 0, [errors]);

   const handleSave = useCallback(() => {
      if (hasErrors) return;

      // const selectedTakaDetails = takaDetailsWithIds.filter((item) => selectedIds.includes(item.id));
      const selectedTakaDetails = takaDetailsWithIds.map((item) => {
         if (selectedIds.includes(item.id)) {
            return {
               ...item,
               quantity3: Number(quantities[item.id] ?? item.quantity3),
               isSelected: true,
               pinvLineDetailId: item?.pinvLineDetailId || item?.purchase_line_detail_id
            };
         }
         return {
            ...item,
            isSelected: false,
         };
      });

      const hasInvalidQuantity = selectedTakaDetails.some(
         (item) => item.isSelected && (item.quantity3 === undefined || item.quantity3 === null || item.quantity3 <= 0)
      );

      if (hasInvalidQuantity) {
         toast({
            title: 'Invalid Quantity',
            description: 'Please ensure all selected items have a quantity greater than 0.',
            status: 'error',
            duration: 3000,
            isClosable: true,
         });
         return;
      }

      console.log("selectedTakaDetails", selectedTakaDetails);
      handleSaveTakaDetail(selectedTakaDetails);

      onClose();
      setSelectedIds([]);
      setQuantities({});
      setErrors({});
   }, [handleSaveTakaDetail, onClose, selectedIds, takaDetailsWithIds, quantities, hasErrors, toast]);

   useEffect(() => {
      if (isOpen && takaDetails.length > 0) {
         setSelectedIds(takaDetails.filter((a) => a.isSelected).map((x) => x.id));
         const initialQuantities = {};
         takaDetails.forEach((item) => {
            initialQuantities[item.id] = item.quantity3;
         });
         setQuantities(initialQuantities);
         setErrors({});
      }
   }, [isOpen, takaDetails]);

   // Color mode values
   const bgColor = useColorModeValue('white', 'gray.800');
   const headerBg = useColorModeValue('blue.50', 'gray.700');
   const borderColor = useColorModeValue('gray.200', 'gray.600');
   const hoverBg = useColorModeValue('gray.50', 'gray.700');
   const selectedBg = useColorModeValue('blue.50', 'blue.900');
   const textColor = useColorModeValue('gray.800', 'white');
   const mutedText = useColorModeValue('gray.600', 'gray.400');

   return (
      <Modal isOpen={isOpen} onClose={handleOnClose} isCentered onCloseComplete={onCloseComplete} size="2xl">
         <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
         <ModalContent bg={bgColor} color={textColor} borderRadius="16px" boxShadow="2xl" maxW="700px">
            <ModalHeader bg={headerBg} borderTopRadius="16px" py={4} px={6}>
               <Flex justifyContent="space-between" alignItems="center" gap={4}>
                  <Flex alignItems="center" gap={3}>
                     <Text fontSize="xl" fontWeight="bold">
                        Select Taka Details
                     </Text>
                     <Badge colorScheme="blue" fontSize="md" px={3} py={1} borderRadius="full" fontWeight="semibold">
                        {selectedIds.length} / {takaDetailsWithIds.length}
                     </Badge>
                  </Flex>
                  <Flex alignItems="center" gap={2}>
                     <Text fontSize="sm" color={mutedText} fontWeight="medium">
                        Total:
                     </Text>
                     <Badge colorScheme="green" fontSize="lg" px={3} py={1} borderRadius="full" fontWeight="bold">
                        {totalSelected}
                     </Badge>
                  </Flex>
               </Flex>
            </ModalHeader>
            <ModalCloseButton top={1} right={1} borderRadius="full" _hover={{ bg: 'red.100', color: 'red.600' }} />

            <Divider />

            <ModalBody p={6}>
               {takaDetailsWithIds.length === 0 ? (
                  <Flex direction="column" alignItems="center" justifyContent="center" py={10} color={mutedText}>
                     <Text fontSize="lg" fontWeight="medium">
                        No taka details available
                     </Text>
                  </Flex>
               ) : (
                  <Box
                     maxH="450px"
                     overflowY="auto"
                     borderRadius="12px"
                     border="1px"
                     borderColor={borderColor}
                     css={{
                        '&::-webkit-scrollbar': {
                           width: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                           background: 'transparent',
                        },
                        '&::-webkit-scrollbar-thumb': {
                           background: '#CBD5E0',
                           borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                           background: '#A0AEC0',
                        },
                     }}>
                     <TableContainer>
                        <Table variant="simple" size="md">
                           <Thead position="sticky" top={0} bg={headerBg} zIndex={1} boxShadow="sm">
                              <Tr>
                                 <Th w="60px" borderBottom="2px" borderColor={borderColor} py={4}>
                                    <Checkbox
                                       isChecked={allSelected}
                                       isIndeterminate={isIndeterminate}
                                       onChange={selectAll}
                                       colorScheme="blue"
                                       size="lg"
                                    />
                                 </Th>
                                 {formType === 'edit' ? (
                                    <Th
                                       isNumeric
                                       borderBottom="2px"
                                       borderColor={borderColor}
                                       fontSize="sm"
                                       textTransform="uppercase"
                                       letterSpacing="wide"
                                       py={4}>
                                       Proceeded further
                                    </Th>
                                 ) : null}
                                 <Th
                                    isNumeric
                                    borderBottom="2px"
                                    borderColor={borderColor}
                                    fontSize="sm"
                                    textTransform="uppercase"
                                    letterSpacing="wide"
                                    py={4}>
                                    Qty
                                 </Th>
                                 <Th
                                    isNumeric
                                    borderBottom="2px"
                                    borderColor={borderColor}
                                    fontSize="sm"
                                    textTransform="uppercase"
                                    letterSpacing="wide"
                                    py={4}>
                                    Max Qty
                                 </Th>
                              </Tr>
                           </Thead>
                           <Tbody>
                              {takaDetailsWithIds.map((taka, index) => {
                                 const isSelected = selectedIds.includes(taka.id);
                                 const isDisabled = taka.minQty3 > 0;
                                 return (
                                    <Tr
                                       key={taka.id}
                                       bg={isSelected ? selectedBg : 'transparent'}
                                       _hover={{ bg: isDisabled ? 'gray.100' : isSelected ? selectedBg : hoverBg }}
                                       opacity={isDisabled ? 0.6 : 1}
                                       cursor={isDisabled ? 'not-allowed' : 'pointer'}
                                       transition="all 0.2s"
                                       onClick={() => {
                                          if (!isDisabled) {
                                             toggleItemSelection(taka.id)
                                          }
                                       }}
                                       borderBottom={index === takaDetailsWithIds.length - 1 ? 'none' : '1px'}
                                       borderColor={borderColor}>
                                       <Td py={4}>
                                          <Checkbox
                                             isChecked={isSelected}
                                             onChange={() => {
                                                if (!isDisabled) {
                                                   toggleItemSelection(taka.id)
                                                }
                                             }}
                                             colorScheme="blue"
                                             size="lg"
                                             onClick={(e) => e.stopPropagation()}
                                          />
                                       </Td>
                                       {formType === 'edit' ? (
                                          <Td isNumeric py={4}>
                                             <Flex justifyContent="flex-end" alignItems="center" gap={2}>
                                                <Text
                                                   fontSize="lg"
                                                   fontWeight={'medium'}
                                                   color={'blue.600'}
                                                   px={2}
                                                   py={1}
                                                   borderRadius="md"
                                                   disabled>
                                                   {taka.minQty3 || 0}
                                                </Text>
                                             </Flex>
                                          </Td>
                                       ) : null}
                                       <Td isNumeric py={4}>
                                          {editingId === taka.id && invoiceType === "issue" ? (
                                             <InputField
                                                type="number"
                                                name="quantity3"
                                                size="sm"
                                                onWheel={(e) => e.target.blur()}
                                                textAlign="right"
                                                value={quantities[taka.id] ?? taka.quantity3}
                                                onChange={(e) => handleQuantityChange(taka.id, e.target.value, taka.maxQty3)}
                                                onBlur={() => setEditingId(null)}
                                                onKeyDown={(e) => {
                                                   if (e.key === 'Enter' || e.key === 'Escape') {
                                                      if (!errors[taka.id]) {
                                                         setEditingId(null);
                                                      }
                                                   }
                                                   e.stopPropagation();
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                                autoFocus
                                                focusBorderColor="blue.400"
                                                _focus={{
                                                   boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.15)',
                                                   borderColor: 'blue.400',
                                                }}
                                                isDisabled={isDisabled}
                                                width="150px"
                                                errors={errors}
                                                errorName={taka.id}
                                             />
                                          ) : (
                                             <Flex justifyContent="flex-end" alignItems="center" gap={2}>
                                                <Text
                                                   fontSize="lg"
                                                   fontWeight={isSelected ? 'bold' : 'medium'}
                                                   color={isSelected ? 'blue.600' : textColor}
                                                   cursor="pointer"
                                                   px={2}
                                                   py={1}
                                                   borderRadius="md"
                                                   _hover={{ bg: 'blue.100' }}
                                                   onClick={(e) => {
                                                      e.stopPropagation();
                                                      if (isDisabled) return;
                                                      setEditingId(taka.id);
                                                   }}>
                                                   {quantities[taka.id] ?? taka.quantity3}
                                                </Text>
                                                {/* {isSelected && <Icon as={CheckIcon} color="blue.500" boxSize={4} />} */}
                                             </Flex>
                                          )}
                                       </Td>
                                       <Td isNumeric py={4}>
                                          <Flex justifyContent="flex-end" alignItems="center" gap={2}>
                                             <Text
                                                fontSize="lg"
                                                fontWeight={'medium'}
                                                color={'blue.600'}
                                                px={2}
                                                py={1}
                                                borderRadius="md"
                                                disabled>
                                                {taka.maxQty3 || 0}
                                             </Text>
                                          </Flex>
                                       </Td>
                                    </Tr>
                                 );
                              })}
                           </Tbody>
                        </Table>
                     </TableContainer>
                  </Box>
               )}
            </ModalBody>

            <Divider />

            <ModalFooter px={6} py={4}>
               <Flex gap={3} width="100%">
                  <Button
                     colorScheme="blue"
                     size="lg"
                     flex={1}
                     leftIcon={<CheckIcon />}
                     onClick={handleSave}
                     isDisabled={selectedIds.length === 0 || hasErrors}
                     _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
                     transition="all 0.2s"
                     borderRadius="10px"
                     fontWeight="bold">
                     Save Selection
                  </Button>
                  <Button
                     colorScheme="gray"
                     variant="outline"
                     size="lg"
                     flex={1}
                     onClick={handleOnClose}
                     _hover={{ bg: 'gray.100', transform: 'translateY(-2px)' }}
                     transition="all 0.2s"
                     borderRadius="10px"
                     fontWeight="medium">
                     Cancel
                  </Button>
               </Flex>
            </ModalFooter>
         </ModalContent>
      </Modal>
   );
};

export default SelectTakaDetail;